import { OptionInfoDto } from "../../../features/options-table/types/OptionInfoDto";
import { PortfolioItemModel } from "../../../features/portfolio-info/types/PortfolioItemModel";
import { useSelectedPortfolioService } from "./useSelectedPortfolioService";
import { dateTimeConversionService } from "../../services/DateTimeConversionService";
import { portfolioMappers } from "../../mappers/PortfolioMappers";
import { BlackScholesModelCalculationService } from "../../services/BlackScholesModelCalculationService";
import { OrderState } from "../../types/OrderState";
import { assetService } from "../../services/AssetService";
import {
    optionParametersSelectorManager
} from "../../../screens/positions-and-orders/utils/OptionParametersSelectorManager";
import { AutoOrderConfigurationDto } from "../../../features/portfolio-info/types/AutoOrderConfigurationDto";
import { AutoOrderConfigurationModel } from "../../../features/portfolio-info/types/AutoOrderConfigurationModel";
import { useAuthContext } from "../../../features/authentication/hooks/use-auth/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { configurationService } from "../../../appConfigService";

export function usePortfolioItemsService() {
    const {
        marketDataDto,
        portfolios,
        selectedPortfolio,
        createPortfolio,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolio,
        setSelectedPortfolioByName,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        refreshPortfolioProfile,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        refreshComparedPortfolioProfile,
        setComparedPortfolioProfile,
        cloneSelectedPortfolio,
        selectedPortfolioDates,
        saveCustomImpliedVolatility,
    } = useSelectedPortfolioService();
    
    const { userAuthData, isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();

    function addOptionToPortfolio(item: OptionInfoDto, size: number): void {
        if (!selectedPortfolio) {
            return;
        }

        const portfolioItemModel: PortfolioItemModel = new PortfolioItemModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Disabled,
            isActive: false,
            locked: false,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: item.ExpireAt,
            impliedVolatilityAtOpen: item.MarkImpliedVolatility,
            customImpliedVolatility: item.MarkImpliedVolatility,
            symbol: item.Symbol,
            size: size,
            openPrice: item.MarkPrice,
            markPrice: item.MarkPrice,
            markImpliedVolatility: item.MarkImpliedVolatility,
            type: item.Type,
            strike: item.Strike,
            underlyingPriceAtOpen: marketDataDto?.UnderlyingPrice ?? 0,
            delta: item.Delta,
            gamma: item.Gamma,
            vega: item.Vega,
            theta: item.Theta,
            comment: null,
            expirationPrice: null,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: item.MarkImpliedVolatility,
            underlyingPriceForPricing: marketDataDto?.UnderlyingPrice ?? 0,
        });

        selectedPortfolio.portfolioItems.push(portfolioItemModel);
        selectedPortfolio.modifiedAt = new Date();
        
        setSelectedPortfolio(selectedPortfolio);
        refreshPortfolioProfile(selectedPortfolio);
    }

    // todo: addItemToPortfolio
    async function addFutureToPortfolio(item: PortfolioItemModel): Promise<void> {
        if (!selectedPortfolio) {
            return;
        }
        
        selectedPortfolio.portfolioItems.push(item);
        selectedPortfolio.modifiedAt = new Date();

        // todo: it is enough to update only item
        updatePortfolioItemsParameters();

        setSelectedPortfolio(selectedPortfolio);
        refreshPortfolioProfile(selectedPortfolio);
        await saveSelectedPortfolio();
    }
    
    async function updateSelectedPortfolio(): Promise<void> {
        if (!selectedPortfolio) {
            return;
        }

        setSelectedPortfolio(selectedPortfolio);
        refreshPortfolioProfile(selectedPortfolio);
        
        if (selectedComparedPortfolio !== null) {
            setComparedPortfolioProfile(comparedPortfolioProfile);
            refreshComparedPortfolioProfile(selectedPortfolio, selectedComparedPortfolio);
        }
    }
    
    async function updatePositionInSelectedPortfolio(
        optionSymbol: string,
        customImpliedVolatility: number,
        customImpliedVolatilityStep?: number | null,
    ): Promise<void> {
        if (!selectedPortfolio) {
            return;
        }

        selectedPortfolio.positions
            .filter((position) => assetService.isOptionType(position.type))
            .forEach((position) => {
                if (position.symbol !== optionSymbol) {
                    return;
                }

                position.customImpliedVolatility = customImpliedVolatility;

                if (customImpliedVolatilityStep !== undefined) {
                    position.customImpliedVolatilityStep = customImpliedVolatilityStep;
                }
            })

        selectedPortfolio.portfolioItems
            .filter((order) => assetService.isOptionType(order.type))
            .forEach((order) => {
                if (order.symbol !== optionSymbol) {
                    return;
                }
    
                order.customImpliedVolatility = customImpliedVolatility;

                if (customImpliedVolatilityStep !== undefined) {
                    order.customImpliedVolatilityStep = customImpliedVolatilityStep;
                }
            })        
        
        setSelectedPortfolio(selectedPortfolio);
        refreshPortfolioProfile(selectedPortfolio);
    }
    
    async function updateItemInSelectedPortfolio(
        refreshItemParameters: boolean = true,
        saveToStorage: boolean = true,
    ): Promise<void> {
        if (!selectedPortfolio) {
            return;
        }
        
        if (refreshItemParameters) {
            updatePortfolioItemsParameters();
        }
        
        setSelectedPortfolio(selectedPortfolio);
        refreshPortfolioProfile(selectedPortfolio);
        
        if (saveToStorage) {
            await saveSelectedPortfolio();
        }
    }

    async function saveAutoOrderConfiguration(
        portfolioItemId: number,
        autoOrderConfiguration: AutoOrderConfigurationDto,
    ): Promise<AutoOrderConfigurationModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        if (portfolioItemId <= 0) {
            return null;
        }

        let configuration: AutoOrderConfigurationModel | null = null;

        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .put<AutoOrderConfigurationDto>(
                `api/portfolio-items/${portfolioItemId}/auto-order-configuration`,
                autoOrderConfiguration,
                {
                    headers: {
                        ...headerValues.appJson,
                        Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`,
                    },
                },
            )
            .then(response => {
                configuration = portfolioMappers.mapAutoOrderConfiguration(response.data);
            })
            .catch((error: any) => {
                console.log(error);
            });

        return configuration;
    }

    async function fetchAutoOrderConfiguration(portfolioItemId: number): Promise<AutoOrderConfigurationModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        if (portfolioItemId <= 0) {
            return null;
        }

        let configuration: AutoOrderConfigurationModel | null = null;

        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .get<AutoOrderConfigurationDto | null>(
                `api/portfolio-items/${portfolioItemId}/auto-order-configuration`,
                {
                    headers: { Authorization: `Bearer ${userAuthData?.jwtToken ?? null}` },
                },
            )
            .then(response => {
                configuration = portfolioMappers.mapAutoOrderConfiguration(response.data);
            })
            .catch((error: any) => {
                console.log(error);
            });

        return configuration;
    }

    async function resetAutoOrderConfigurationState(portfolioItemId: number): Promise<AutoOrderConfigurationModel | null> {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return null;
        }

        if (portfolioItemId <= 0) {
            return null;
        }

        let configuration: AutoOrderConfigurationModel | null = null;

        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .put<AutoOrderConfigurationDto>(
                `api/portfolio-items/${portfolioItemId}/auto-order-configuration/state/reset`,
                null,
                {
                    headers: {
                        ...headerValues.appJson,
                        Authorization: `Bearer ${userAuthData?.jwtToken ?? null}`,
                    },
                },
            )
            .then(response => {
                configuration = portfolioMappers.mapAutoOrderConfiguration(response.data);
            })
            .catch((error: any) => {
                console.log(error);
            });

        return configuration;
    }

    function updatePortfolioItemsParameters() {
        if (!selectedPortfolio) {
            return;
        }
                
        selectedPortfolio.portfolioItems.map(x => {
            const optionInfoDto = optionParametersSelectorManager.getOption(
                marketDataDto!.OptionInfos, x.type, x.expireAt!, x.strike!);
            
            x.markImpliedVolatility = optionInfoDto?.MarkImpliedVolatility ?? 0;
            
            const riskFreeInterestValue = optionInfoDto?.RiskFreeInterest
                ?? BlackScholesModelCalculationService.riskFreeRate;
            
            const { price, delta, gamma, vega, theta } =
                portfolioMappers.getAssetParameters(x.type, x.strike, x.expireAt,
                    x.markImpliedVolatility, riskFreeInterestValue, marketDataDto!.UnderlyingPrice);
            
            x.markPrice = price;
            x.delta = delta * x.size;
            x.gamma = (gamma ?? 0) * x.size;
            x.vega = (vega ?? 0) * x.size;
            x.theta = (theta ?? 0) * x.size;
        });
    }
    
    async function deleteItemFromSelectedPortfolio(internalId: string): Promise<void> {
        if (!selectedPortfolio && !selectedPortfolio) {
            return;
        }

        const newPortfolioItems: PortfolioItemModel[] = [];

        selectedPortfolio.portfolioItems.map(x => {
            if (x.internalId != internalId) {
                newPortfolioItems.push(x);
            }
        });

        selectedPortfolio.portfolioItems = newPortfolioItems;
        selectedPortfolio.modifiedAt = new Date();
        
        setSelectedPortfolio(selectedPortfolio);
        refreshPortfolioProfile(selectedPortfolio);
        
        await saveSelectedPortfolio();
    }

    return {
        marketDataDto,
        portfolios,
        selectedPortfolio,
        createPortfolio,
        portfolioProfile,
        comparedPortfolioProfile,
        setSelectedPortfolio,
        setSelectedPortfolioByName,
        editSelectedPortfolio,
        saveSelectedPortfolio,
        fetchSelectedPortfolio,
        deleteSelectedPortfolio,
        refreshPortfolioProfile,
        addOptionToPortfolio,
        addFutureToPortfolio,
        updateSelectedPortfolio,
        updateItemInSelectedPortfolio,
        deleteItemFromSelectedPortfolio,
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        cloneSelectedPortfolio,
        selectedPortfolioDates,
        updatePositionInSelectedPortfolio,
        saveCustomImpliedVolatility,
        saveAutoOrderConfiguration,
        fetchAutoOrderConfiguration,
        resetAutoOrderConfigurationState,
    };
}

const headerValues = {
    appJson: { 'Content-Type': 'application/json' },
};
